import { useCallback, useEffect, useRef, useState } from 'react';
import { NotificationView } from '../api';
import { useApp } from '../App';
import { useChat } from '../chat/ChatProvider';
import { lookOf, type NotifyLook } from '../chat/notificationView';
import { isSilent, openNotification } from '../chat/openNotification';
import { useIsMobile } from '../lib/media';
import { notifyTime } from '../chat/format';
import { IconBell, IconCalendar, IconCamera, IconCheck, IconClose, IconFile, IconMention } from '../components/icons';
import { playSoundOnce } from '../lib/sounds';

/** Суть уведомления приходит с сервера строчной («назначила вас…»), а строка начинается с неё. */


/*
 * Как выглядит уведомление, решает общий модуль `chat/notificationView`: он же
 * питает карточки в чате уведомлений. Здесь остаётся раскладка строки списка
 * (дизайн-система 3.18) и подбор рисунка по имени значка.
 */
function iconOf(icon: NotifyLook['icon']) {
  if (icon === 'task') return <IconCheck />;
  if (icon === 'calendar') return <IconCalendar />;
  if (icon === 'bell') return <IconBell />;
  if (icon === 'call') return <IconCamera />;
  if (icon === 'mention') return <IconMention />;
  return <IconFile />;
}

export function Bell({ variant }: { variant: 'desktop' | 'mobile' }) {
  const app = useApp();
  const chat = useChat();
  const mobile = useIsMobile();
  const items = chat.notifications;
  const unread = chat.notifUnread;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left?: number; right?: number; top: number }>({ top: 60 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const readTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  /** Сколько непрочитанных было на прошлой проверке; null — проверок ещё не было. */
  const seenUnread = useRef<number | null>(null);

  /*
   * Список и счётчик живут в провайдере чата: тот же источник питает чат
   * уведомлений, поэтому прочитанное в одном месте сразу видно в другом.
   * Здесь остаётся только звук на прибавку непрочитанных.
   */
  useEffect(() => {
    /*
     * Считаем только с первого настоящего ответа сервера. До него счётчик
     * равен нулю не потому, что непрочитанного нет, а потому, что его ещё не
     * спрашивали: прежде первый же ответ читался как прибавка, и звук встречал
     * каждое открытие приложения и каждую новую вкладку (жалоба Ильи
     * 21.09.2026).
     *
     * Сам звук — один на все вкладки: `playSoundOnce` с номером самого
     * свежего уведомления.
     */
    if (!chat.notifLoaded) return;
    if (seenUnread.current !== null && unread > seenUnread.current) {
      playSoundOnce('notify', String(items[0]?.id ?? unread));
    }
    seenUnread.current = unread;
  }, [chat.notifLoaded, items, unread]);

  const load = chat.loadNotifications;

  const markRead = useCallback(
    (id: number) => {
      void chat.markNotificationsRead({ id });
    },
    [chat],
  );

  // Прочтение при попадании в видимую зону открытого окна (≥60% высоты, 700 мс).
  const checkVisible = useCallback(() => {
    const list = listRef.current;
    if (!list || !open) return;
    const lr = list.getBoundingClientRect();
    for (const el of list.querySelectorAll<HTMLElement>('.ntf.unread')) {
      const id = Number(el.dataset.id);
      if (readTimers.current.has(id)) continue;
      const r = el.getBoundingClientRect();
      const visible = Math.min(r.bottom, lr.bottom) - Math.max(r.top, lr.top);
      if (visible >= r.height * 0.6) {
        readTimers.current.set(
          id,
          setTimeout(() => {
            readTimers.current.delete(id);
            if (listRef.current) markRead(id);
          }, 700),
        );
      }
    }
  }, [open, markRead]);

  useEffect(() => {
    if (open) setTimeout(checkVisible, 300);
    else {
      readTimers.current.forEach(clearTimeout);
      readTimers.current.clear();
    }
  }, [open, checkVisible]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('.ntf-pop') || t.closest('.sb-bell')) return;
      setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      if (window.innerWidth <= 900) setPos({ right: 12, top: r.bottom + 8 });
      else
        setPos({
          left: Math.min(r.right + 10, window.innerWidth - 346),
          top: Math.max(12, Math.min(r.top - 8, window.innerHeight - 400)),
        });
    }
    setOpen(true);
    load();
  };

  const onNtfClick = (n: NotificationView) => {
    markRead(n.id);
    // Раздел выключен на телефоне: уведомление только отмечается прочитанным
    // и никуда не ведёт (дизайн-система 3.49). Окно остаётся открытым —
    // закрывать его незачем, перехода всё равно нет.
    if (isSilent(app, n, mobile)) return;
    setOpen(false);
    // Куда ведёт уведомление, решает общий модуль: колокольчик и лента чата
    // должны открывать одно и то же событие одинаково.
    openNotification(app, n);
  };

  const sorted = [...items].sort((a, b) => b.ts - a.ts);

  return (
    <>
      <button
        ref={btnRef}
        className="ibtn has-counter sb-bell"
        title="Уведомления"
        aria-label="Уведомления"
        data-variant={variant}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
      >
        <IconBell />
        {unread > 0 && <span className="counter sm">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="menu ntf-pop" style={pos} role="dialog" aria-label="Уведомления">
          <div className="panel-head">
            <div className="title">Уведомления</div>
            <button
              type="button"
              className="btn link sm"
              onClick={() => void chat.markNotificationsRead({ all: true })}
            >
              Прочитать все
            </button>
            <button className="ibtn round close m-only" aria-label="Закрыть" onClick={() => setOpen(false)}>
              <IconClose />
            </button>
          </div>
          <div className="ntf-list" ref={listRef} onScroll={() => setTimeout(checkVisible, 250)}>
            {sorted.length === 0 && <div className="empty">Уведомлений пока нет.</div>}
            {sorted.map((n) => {
              const p = n.proc ? app.procMap[n.proc] : null;
              const kind = lookOf(n, () => (p ? p.name : ''));
              const quiet = isSilent(app, n, mobile);
              return (
                <button
                  type="button"
                  key={n.id}
                  className={`notif ntf ${n.read ? 'read' : 'unread'}`}
                  data-id={n.id}
                  onClick={() => onNtfClick(n)}
                >
                  <span className={`notif-icon${quiet ? ' neutral' : kind.tone ? ` ${kind.tone}` : ''}`}>
                    {iconOf(kind.icon)}
                  </span>
                  <span className="notif-text">
                    <span className="notif-title">{kind.title}</span>
                    <span className="notif-sub">{kind.sub}</span>
                    {quiet ? (
                      <span className="notif-meta">
                        <span className="notif-time">{notifyTime(n.ts)}</span>
                        <span className="notif-note">· Открывается на компьютере</span>
                      </span>
                    ) : (
                      <span className="notif-time">{notifyTime(n.ts)}</span>
                    )}
                  </span>
                  <span className="notif-dot" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
